import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import axios from 'axios';
import { Trophy, Crown, Medal, Award } from 'lucide-react';
import { maskDisplayName } from '@/lib/maskDisplayName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: '#131413',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  card: 'rgba(19,20,19,0.9)',
};

const RANK_CONFIG = {
  1: { label: '#C9A84C', bg: 'rgba(201,168,76,0.06)', border: 'rgba(201,168,76,0.3)', glow: 'rgba(201,168,76,0.12)', Icon: Crown },
  2: { label: '#9EAFAC', bg: 'rgba(158,175,172,0.06)', border: 'rgba(158,175,172,0.25)', glow: 'rgba(158,175,172,0.08)', Icon: Medal },
  3: { label: '#C07C43', bg: 'rgba(192,124,67,0.06)', border: 'rgba(192,124,67,0.25)', glow: 'rgba(192,124,67,0.08)', Icon: Award },
};

function getLeaderStats(leader, fallbackRank) {
  const positions = Number(leader?.matrix_positions_count || 0);
  const grossVolume = Number(leader?.matrix_gross_volume || 0);
  const earnings = Number(leader?.total_earnings || 0);
  const progress = Number(leader?.progress_pct || 0);
  const name = leader?.name || 'Miembro VaultGrow';
  const displayRank = leader?.rank || fallbackRank;

  return { positions, grossVolume, earnings, progress, name, displayRank };
}

export default function LeaderboardPage() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    axios.get(`${API}/leaderboard`)
      .then(res => {
        setLeaders(Array.isArray(res.data) ? res.data : []);
        setError(null);
      })
      .catch(() => {
        setError('No se pudo cargar el ranking.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <Navbar />
      <div style={{ padding: isMobile ? '100px 16px 64px' : '112px 24px 64px', maxWidth: '760px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '20px', background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trophy size={32} color={C.accentDark} />
          </div>
          <h1 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: 'clamp(36px, 5vw, 56px)', color: 'white', marginBottom: '10px', letterSpacing: '-1px' }}>Top Afiliados</h1>
          <p style={{ color: C.accentDark, fontSize: '16px' }}>Cargando avance de ciclos...</p>
        </div>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ height: '72px', borderRadius: '16px', border: `1px solid ${C.border}`, background: 'rgba(158,175,172,0.04)', marginBottom: '10px' }} />
        ))}
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <Navbar />
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 24px 64px' }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center', background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '32px 24px', boxShadow: '0 0 32px rgba(158,175,172,0.08)' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'rgba(158,175,172,0.06)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <Trophy size={28} color={C.accentDark} />
          </div>
          <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '24px', color: 'white', marginBottom: '8px' }}>No se pudo cargar el ranking</h2>
          <p style={{ color: C.accentDark, lineHeight: 1.5 }}>Intenta nuevamente en unos minutos.</p>
        </div>
      </div>
    </div>
  );

  const top3 = leaders.slice(0, 3);
  const rest = leaders.slice(3);
  // podium order: 2nd, 1st, 3rd
  const podium = [top3[1], top3[0], top3[2]];
  const podiumRanks = [2, 1, 3];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      <div style={{ paddingTop: isMobile ? '88px' : '100px', paddingBottom: '64px', padding: isMobile ? '88px 16px 64px' : '100px 24px 64px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '64px', animation: 'fade-up 0.6s ease both' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '72px', height: '72px', borderRadius: '20px',
                background: 'linear-gradient(135deg, #C9A84C, #8B6030)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 32px rgba(201,168,76,0.25)',
              }}>
                <Trophy size={34} color="#131413" />
              </div>
            </div>
            <p style={{ color: C.accentDark, fontSize: '12px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '12px' }}>Ranking Global</p>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: 'clamp(36px, 5vw, 56px)', color: 'white', marginBottom: '10px', letterSpacing: '-1px' }}>Top Afiliados</h1>
            <p style={{ color: C.accentDark, fontSize: '16px' }}>Los ciclos con mayor avance dentro de la red VaultGrow</p>
          </div>

          {/* Podium Top 3 */}
          {top3.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '32px',
              alignItems: 'flex-end',
              justifyItems: 'center',
            }}>
              {podium.map((leader, idx) => {
                if (!leader) return <div key={idx} />;
                const rank = podiumRanks[idx];
                const cfg = RANK_CONFIG[rank];
                const isFirst = rank === 1;
                const RankIcon = cfg.Icon;
                const { positions, grossVolume, earnings, progress, name, displayRank } = getLeaderStats(leader, rank);
                return (
                  <div
                    key={displayRank}
                    data-testid={`leaderboard-rank-${displayRank}`}
                    title={`Volumen bruto: $${grossVolume.toFixed(2)}`}
                    style={{
                      position: 'relative',
                      background: cfg.bg,
                      border: `1px solid ${cfg.border}`,
                      borderRadius: '20px',
                      padding: isFirst ? '28px 20px' : '22px 16px',
                      width: isMobile ? '100%' : 'auto',
                      maxWidth: isMobile ? '360px' : 'none',
                      textAlign: 'center',
                      boxShadow: `0 0 24px ${cfg.glow}`,
                      transform: !isMobile && isFirst ? 'translateY(-12px)' : 'none',
                      animation: `fade-up 0.6s ease both`,
                      animationDelay: `${idx * 0.1}s`,
                    }}
                  >
                    {isFirst && (
                      <div style={{ position: 'absolute', top: '-16px', left: '50%', transform: 'translateX(-50%)' }}>
                        <Crown size={28} color="#C9A84C" />
                      </div>
                    )}
                    {/* Avatar */}
                    <div style={{
                      width: isFirst ? '60px' : '50px',
                      height: isFirst ? '60px' : '50px',
                      borderRadius: '50%',
                      margin: '0 auto 12px',
                      background: 'rgba(158,175,172,0.1)',
                      border: `2px solid ${cfg.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: isFirst ? '20px' : '16px', color: 'white' }}>
                        {name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    {/* Rank badge */}
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: isFirst ? '26px' : '20px', color: cfg.label, marginBottom: '4px' }}>#{displayRank}</div>
                    <div style={{ color: 'white', fontWeight: 600, fontSize: '14px', marginBottom: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {maskDisplayName(name)}
                    </div>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: isFirst ? '24px' : '18px', color: cfg.label }}>{positions} posiciones</div>
                    <div style={{ color: C.accentDark, fontSize: '11px', marginBottom: '8px' }}>posiciones completadas</div>
                    <div style={{ color: C.accent, fontSize: '13px', fontWeight: 600 }}>${earnings.toFixed(2)} acumulados</div>
                    <div style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: '50px', padding: '3px 10px' }}>
                      <RankIcon size={10} color={cfg.label} />
                      <span style={{ fontSize: '11px', color: C.accentDark }}>{progress.toFixed(0)}% del ciclo</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Ranks 4–10 */}
          {rest.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="leaderboard-list">
              {rest.map((leader, idx) => {
                const { positions, grossVolume, earnings, progress, name, displayRank } = getLeaderStats(leader, idx + 4);
                return (
                  <div
                    key={displayRank}
                    title={`Volumen bruto: $${grossVolume.toFixed(2)}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '16px',
                      background: C.card, border: `1px solid ${C.border}`,
                      borderRadius: '14px', padding: isMobile ? '14px' : '16px 20px',
                      transition: 'all 0.2s',
                      animation: `fade-up 0.5s ease both`,
                      animationDelay: `${(idx + 3) * 0.06}s`,
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = 'rgba(158,175,172,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.card; }}
                  >
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      background: 'rgba(158,175,172,0.06)', border: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, color: C.accentDark, fontSize: '14px' }}>#{displayRank}</span>
                    </div>

                    {/* Avatar */}
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '13px', color: C.accent }}>
                        {name.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'white', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{maskDisplayName(name)}</div>
                      <div style={{ color: C.accentDark, fontSize: '12px' }}>{positions} posiciones</div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '14px' : '24px', flexShrink: 0, marginLeft: isMobile ? '52px' : '0', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, color: C.accent }}>{positions}</div>
                        <div style={{ color: C.accentDark, fontSize: '11px' }}>posiciones</div>
                      </div>
                      <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
                        <div style={{ color: 'white', fontWeight: 600 }}>${earnings.toFixed(2)}</div>
                        <div style={{ color: C.accentDark, fontSize: '11px' }}>acumulados</div>
                      </div>
                      <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
                        <div style={{ color: C.accent, fontWeight: 700 }}>{progress.toFixed(0)}%</div>
                        <div style={{ color: C.accentDark, fontSize: '11px' }}>del ciclo</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {leaders.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 24px', animation: 'fade-up 0.6s ease both' }}>
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(158,175,172,0.06)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <Trophy size={32} color={C.accentDark} />
              </div>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', color: C.accentDark, marginBottom: '8px' }}>Aún no hay ciclos activos</h3>
              <p style={{ color: C.accentDark, fontSize: '14px', opacity: 0.7 }}>El ranking se llenará cuando los miembros comiencen a completar posiciones dentro de sus ciclos.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
